import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import {
  FileText, BarChart3, Sparkles, Download, Save, Trash2, ArrowLeft, Loader2,
  Image as ImageIcon, Palette, Eye, ExternalLink,
} from "lucide-react";

const PRODUCT_ICON = { standard: FileText, infographic: BarChart3 };
const FORMATS = [
  { key: "pdf", label: "PDF" },
  { key: "pptx", label: "PowerPoint" },
  { key: "html", label: "Web (HTML)" },
];

// Auth-protected image (branding assets require a bearer token, so <img src> can't load them directly)
function AuthImg({ src, alt, className, testid }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true, obj = null;
    if (!src) { setUrl(null); return; }
    const path = src.replace(/^\/api/, "");
    api.get(path, { responseType: "blob" }).then((r) => {
      if (!alive) return; obj = URL.createObjectURL(r.data); setUrl(obj);
    }).catch(() => {});
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [src]);
  if (!url) return <div className={className} data-testid={testid} />;
  return <img src={url} alt={alt} className={className} data-testid={testid} />;
}

async function downloadBlob(rid, format, name) {
  try {
    const r = await api.get(`/report-studio/${rid}/export?format=${format}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `${(name || "report").replace(/[^a-z0-9]+/gi, "-")}.${format}`;
    a.click();
    toast.success(`${format.toUpperCase()} downloaded`);
  } catch { toast.error("Download failed"); }
}

export default function ReportStudio() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [branding, setBranding] = useState({ accent: "#0F7B4E", logo_url: null, icon_url: null });
  const [reports, setReports] = useState(null);
  const [view, setView] = useState("home");

  // build state
  const [product, setProduct] = useState("standard");
  const [period, setPeriod] = useState("");
  const [compare, setCompare] = useState(true);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [savingBrand, setSavingBrand] = useState(false);

  // preview state
  const [active, setActive] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [saveFmt, setSaveFmt] = useState("pdf");
  const logoRef = useRef(); const iconRef = useRef();

  const loadReports = () => api.get("/report-studio").then((r) => setReports(r.data.reports)).catch(() => setReports([]));

  useEffect(() => {
    api.get("/report-studio/config").then(({ data }) => {
      setConfig(data);
      setBranding(data.branding || { accent: "#0F7B4E" });
      setPeriod(data.periods?.[0] || "");
    }).catch(() => toast.error("Could not load Report Studio"));
    loadReports();
  }, []);

  const saveBranding = async () => {
    setSavingBrand(true);
    try {
      const { data } = await api.post("/report-studio/branding", { accent: branding.accent });
      setBranding(data); toast.success("Branding saved — reused across all reports");
    } catch { toast.error("Could not save branding (admin/ESG-manager only)"); }
    finally { setSavingBrand(false); }
  };

  const uploadBrand = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData(); fd.append("kind", kind); fd.append("file", file);
      const { data } = await api.post("/report-studio/branding/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBranding((b) => ({ ...b, [`${kind}_url`]: data.url }));
      toast.success(`${kind === "logo" ? "Logo" : "Icon"} uploaded`);
    } catch { toast.error("Upload failed — PNG/JPG/WebP/SVG under 5MB (admin/ESG-manager only)"); }
    finally { setUploading(null); }
  };

  const openPreview = async (row) => {
    setBusy(true);
    try {
      const r = await api.get(`/report-studio/${row.id}/preview.html`, { responseType: "text", transformResponse: [(x) => x] });
      setActive(row); setPreviewHtml(r.data); setView("preview");
    } catch { toast.error("Could not load preview"); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    if (!period) return;
    setBusy(true);
    try {
      const { data } = await api.post("/report-studio", { product, period, compare, title: title || null });
      loadReports();
      await openPreview(data);
      toast.success("Report generated");
    } catch { toast.error("Could not generate report"); }
    finally { setBusy(false); }
  };

  const saveToLibrary = async (rid, fmt) => {
    try {
      await api.post(`/report-studio/${rid}/save-to-library?format=${fmt}`);
      toast.success("Saved to Document Inbox");
      loadReports();
    } catch { toast.error("Could not save to library"); }
  };

  const del = async (rid) => {
    if (!window.confirm("Delete this report?")) return;
    try { await api.delete(`/report-studio/${rid}`); loadReports(); if (active?.id === rid) setView("home"); }
    catch { toast.error("Could not delete"); }
  };

  const openInNewWindow = () => {
    if (!previewHtml) return;
    const blob = new Blob([previewHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  // ---------------- Preview view ----------------
  if (view === "preview" && active) {
    return (
      <div className="space-y-4" data-testid="rs-preview-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => setView("home")} data-testid="rs-back"><ArrowLeft className="h-4 w-4" /> Back</Button>
            <div>
              <div className="font-semibold text-sm flex items-center gap-2">
                {active.title || `${active.entity} — ${active.period}`}
                <Badge variant="outline" className="text-[9px] uppercase">{active.product}</Badge>
              </div>
              <div className="text-[11px] text-muted-foreground">Evidence coverage {active.coverage_pct ?? "—"}%</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={openInNewWindow} data-testid="rs-open-window"><ExternalLink className="h-3.5 w-3.5" /> Open in new window</Button>
            {FORMATS.map((f) => (
              <Button key={f.key} size="sm" variant="outline" onClick={() => downloadBlob(active.id, f.key, active.title || `${active.entity}-${active.period}`)} data-testid={`rs-download-${f.key}`}>
                <Download className="h-3.5 w-3.5" /> {f.label}
              </Button>
            ))}
            <div className="flex items-center gap-1 border-l border-border pl-2">
              <Select value={saveFmt} onValueChange={setSaveFmt}>
                <SelectTrigger className="h-8 w-[130px]" data-testid="rs-save-format"><SelectValue /></SelectTrigger>
                <SelectContent>{FORMATS.map((f) => <SelectItem key={f.key} value={f.key} data-testid={`rs-savefmt-${f.key}`}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" variant="primary" onClick={() => saveToLibrary(active.id, saveFmt)} data-testid="rs-save-library-btn"><Save className="h-3.5 w-3.5" /> Save to library</Button>
            </div>
          </div>
        </div>
        <div className="card-flat p-0 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-surface/60">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
              <span className="ml-2 font-mono">{active.title || `${active.entity} · FY${active.period}`}</span>
            </div>
            <button onClick={openInNewWindow} className="text-[11px] text-accent hover:underline flex items-center gap-1" data-testid="rs-frame-popout"><ExternalLink className="h-3 w-3" /> Full size</button>
          </div>
          <iframe title="report-preview" srcDoc={previewHtml} className="w-full h-[74vh] bg-white block" data-testid="rs-preview-frame" />
        </div>
      </div>
    );
  }

  // ---------------- Home view ----------------
  return (
    <div className="space-y-6" data-testid="rs-page">
      <div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Sparkles className="h-7 w-7 text-accent" /> Report Studio</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-3xl">Two branded report products from your live platform data. Reuse one logo &amp; icon across every report, then export to PDF, PowerPoint or a self-contained web page — and save into your Document Inbox.</p>
      </div>

      {/* Branding — reusable */}
      <div className="card-flat p-5" data-testid="rs-branding-card">
        <div className="label-eyebrow mb-3 flex items-center gap-2"><Palette className="h-4 w-4" /> Brand kit — reused across all reports</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Logo (landscape)</div>
            <div className="flex items-center gap-3">
              <AuthImg src={branding.logo_url} alt="logo" className="h-12 w-24 object-contain border border-border rounded bg-white" testid="rs-logo-preview" />
              <div className="space-y-1">
                <Button size="sm" variant="outline" onClick={() => logoRef.current?.click()} disabled={uploading === "logo"} data-testid="rs-branding-logo-upload">
                  {uploading === "logo" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />} Upload logo
                </Button>
                <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => uploadBrand("logo", e.target.files[0])} />
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Icon (square)</div>
            <div className="flex items-center gap-3">
              <AuthImg src={branding.icon_url} alt="icon" className="h-12 w-12 object-contain border border-border rounded bg-white" testid="rs-icon-preview" />
              <div className="space-y-1">
                <Button size="sm" variant="outline" onClick={() => iconRef.current?.click()} disabled={uploading === "icon"} data-testid="rs-branding-icon-upload">
                  {uploading === "icon" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />} Upload icon
                </Button>
                <input ref={iconRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => uploadBrand("icon", e.target.files[0])} />
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Accent colour</div>
            <div className="flex items-center gap-2">
              <input type="color" value={branding.accent || "#0F7B4E"} onChange={(e) => setBranding((b) => ({ ...b, accent: e.target.value }))} className="h-9 w-12 rounded border border-border bg-transparent cursor-pointer" data-testid="rs-branding-accent" />
              <input value={branding.accent || ""} onChange={(e) => setBranding((b) => ({ ...b, accent: e.target.value }))} className="w-28 text-sm rounded-md border border-border bg-background p-1.5 font-mono" data-testid="rs-branding-accent-hex" />
              <Button size="sm" variant="outline" onClick={saveBranding} disabled={savingBrand} data-testid="rs-branding-save">{savingBrand ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Create a report */}
      <div className="card-flat p-5 space-y-4" data-testid="rs-build-card">
        <div className="label-eyebrow">Create a report</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(config?.products || []).map((p) => {
            const Icon = PRODUCT_ICON[p.key] || FileText;
            const on = product === p.key;
            return (
              <button key={p.key} onClick={() => setProduct(p.key)} data-testid={`rs-product-${p.key}`}
                className={`text-left border rounded-lg p-4 transition-colors ${on ? "border-accent bg-accent/5 ring-1 ring-accent" : "border-border hover:bg-surface"}`}>
                <div className="flex items-center gap-2 font-medium"><Icon className={`h-5 w-5 ${on ? "text-accent" : "text-muted-foreground"}`} /> {p.name}{on && <Badge variant="success" className="ml-auto text-[9px]">Selected</Badge>}</div>
                <div className="text-xs text-muted-foreground mt-1.5">{p.desc}</div>
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Reporting period (FY)</div>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-full" data-testid="rs-period-select"><SelectValue placeholder="Select year" /></SelectTrigger>
              <SelectContent>{(config?.periods || []).map((p) => <SelectItem key={p} value={p} data-testid={`rs-period-${p}`}>FY{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Report title (optional)</div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`${config?.entity || "Company"} Sustainability Report ${period}`}
              className="w-full text-sm rounded-md border border-border bg-background p-2" data-testid="rs-title-input" />
          </div>
          <div className="flex items-end">
            <button onClick={() => setCompare((v) => !v)} data-testid="rs-compare-toggle"
              className="flex items-center gap-2 text-sm">
              <span className={`h-4 w-4 rounded border flex items-center justify-center text-[10px] ${compare ? "bg-accent border-accent text-white" : "border-muted-foreground"}`}>{compare ? "✓" : ""}</span>
              Show prior-year comparison
            </button>
          </div>
        </div>
        <div className="flex justify-end">
          <Button variant="primary" onClick={generate} disabled={busy || !period} data-testid="rs-generate-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate &amp; preview
          </Button>
        </div>
      </div>

      {/* Library */}
      <div className="card-flat p-5">
        <div className="label-eyebrow mb-3">Generated reports</div>
        {reports === null ? <div className="py-8 text-center text-muted-foreground text-sm">Loading…</div>
          : reports.length === 0 ? (
            <div className="py-10 text-center">
              <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground text-sm">No reports yet — pick a product above and generate your first branded report.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {reports.map((r) => {
                const Icon = PRODUCT_ICON[r.product] || FileText;
                return (
                  <div key={r.id} className="flex items-center justify-between gap-3 border border-border rounded-md p-3 flex-wrap" data-testid={`rs-report-row-${r.id}`}>
                    <div className="min-w-0 flex items-center gap-2.5">
                      <Icon className="h-4 w-4 text-accent shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{r.title || `${r.entity} · FY${r.period}`}</div>
                        <div className="text-[11px] text-muted-foreground capitalize">{r.product} · coverage {r.coverage_pct ?? "—"}%{r.saved_format ? ` · saved (${r.saved_format})` : ""}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => openPreview(r)} data-testid={`rs-open-${r.id}`}><Eye className="h-3.5 w-3.5" /> Preview</Button>
                      <Button size="sm" variant="outline" onClick={() => downloadBlob(r.id, "pdf", r.title || `${r.entity}-${r.period}`)} data-testid={`rs-row-pdf-${r.id}`}><Download className="h-3.5 w-3.5" /> PDF</Button>
                      <Button size="sm" variant="outline" onClick={() => saveToLibrary(r.id, "pdf")} data-testid={`rs-row-save-${r.id}`}><Save className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(r.id)} data-testid={`rs-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
}
